/**
 * SRP (Search Results Page) Allowlist
 *
 * Defines which search result pages are indexable by search engines.
 * Only pages in this allowlist will have:
 * - robots: index,follow
 * - Self-referencing canonical
 * - Proper hreflang cluster
 *
 * All other pages will be noindex,follow
 */

import { encodeSrpPathForUrl } from '../utils/routing/encode-srp-path-for-url';
import { CATEGORY_HUB_CONFIG } from '../utils/category/category-hub-config';
import { ROLE_HUB_CONFIG, buildRoleHubPath } from '../utils/role/role-hub-config';
import { getFinnishRoleGenitive } from '../utils/i18n/finnish-role-genitive';

export interface SRPHub {
  type: 'location' | 'role' | 'category' | 'freelance';
  localeUrls: {
    en: string;
    fi: string;
    sv: string;
  };
  title: {
    en: string;
    fi: string;
    sv: string;
  };
  description: {
    en: string;
    fi: string;
    sv: string;
  };
  lastModified: string; // ISO date string
}

/**
 * Major Finnish cities
 */
export const cityHubs: SRPHub[] = [
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/helsinki_finland',
      fi: '/fi/tyopaikat/alue/helsinki_suomi',
      sv: '/sv/lediga-jobb/omrade/helsingfors_finland'
    },
    title: {
      en: 'Jobs in Helsinki',
      fi: 'Työpaikat Helsingissä',
      sv: 'Jobb i Helsingfors'
    },
    description: {
      en: 'Discover open positions in Helsinki, Finland\'s vibrant capital. From tech startups to established corporations, find your next career opportunity in the Nordic hub of innovation and design.',
      fi: 'Löydä avoimia työpaikkoja Helsingistä, Suomen elinvoimaisesta pääkaupungista. Teknologiastartupeista vakiintuneisiin yrityksiin - löydä seuraava urasi innovaation ja muotoilun pohjoismaisen keskuksen ytimessä.',
      sv: 'Upptäck öppna tjänster i Helsingfors, Finlands livliga huvudstad. Från tech-startups till etablerade företag, hitta din nästa karriärmöjlighet i det nordiska navet för innovation och design.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/espoo_finland',
      fi: '/fi/tyopaikat/alue/espoo_suomi',
      sv: '/sv/lediga-jobb/omrade/esbo_finland'
    },
    title: {
      en: 'Jobs in Espoo',
      fi: 'Työpaikat Espoossa',
      sv: 'Jobb i Esbo'
    },
    description: {
      en: 'Explore career opportunities in Espoo, home to leading tech companies, research institutions, and innovative startups. Find your place in Finland\'s second-largest city and a major tech hub.',
      fi: 'Tutki uramahdollisuuksia Espoossa, johtavien teknologiayritysten, tutkimuslaitosten ja innovatiivisten startup-yritysten kotikaupungissa. Löydä paikkasi Suomen toiseksi suurimmasta kaupungista ja merkittävästä teknologiakeskuksesta.',
      sv: 'Utforska karriärmöjligheter i Esbo, hem för ledande teknikföretag, forskningsinstitutioner och innovativa startups. Hitta din plats i Finlands näst största stad och ett stort teknikcentrum.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/tampere_finland',
      fi: '/fi/tyopaikat/alue/tampere_suomi',
      sv: '/sv/lediga-jobb/omrade/tammerfors_finland'
    },
    title: {
      en: 'Jobs in Tampere',
      fi: 'Työpaikat Tampereella',
      sv: 'Jobb i Tammerfors'
    },
    description: {
      en: 'Find jobs in Tampere, Finland\'s third-largest city known for its vibrant culture, growing tech scene, and excellent quality of life. Explore opportunities in manufacturing, technology, and creative industries.',
      fi: 'Löydä työpaikkoja Tampereelta, Suomen kolmanneksi suurimmasta kaupungista, joka tunnetaan elinvoimaisesta kulttuuristaan, kasvavasta teknologia-alastaan ja erinomaisesta elämänlaadusta. Tutustu mahdollisuuksiin teollisuudessa, teknologiassa ja luovilla aloilla.',
      sv: 'Hitta jobb i Tammerfors, Finlands tredje största stad känd för sin livliga kultur, växande teknikscen och utmärkta livskvalitet. Utforska möjligheter inom tillverkning, teknik och kreativa branscher.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/vantaa_finland',
      fi: '/fi/tyopaikat/alue/vantaa_suomi',
      sv: '/sv/lediga-jobb/omrade/vanda_finland'
    },
    title: {
      en: 'Jobs in Vantaa',
      fi: 'Työpaikat Vantaalla',
      sv: 'Jobb i Vanda'
    },
    description: {
      en: 'Discover career opportunities in Vantaa, part of the Greater Helsinki metropolitan area. Home to Helsinki Airport and major logistics hubs, Vantaa offers diverse opportunities in aviation, logistics, and services.',
      fi: 'Löydä uramahdollisuuksia Vantaalta, osana pääkaupunkiseutua. Helsinki-Vantaan lentokentän ja merkittävien logistiikkakeskusten kotikaupunki tarjoaa monipuolisia mahdollisuuksia ilmailussa, logistiikassa ja palveluissa.',
      sv: 'Upptäck karriärmöjligheter i Vanda, del av Helsingfors storstadsområde. Hem för Helsingfors flygplats och stora logistikcentra, Vanda erbjuder olika möjligheter inom flyg, logistik och tjänster.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/turku_finland',
      fi: '/fi/tyopaikat/alue/turku_suomi',
      sv: '/sv/lediga-jobb/omrade/abo_finland'
    },
    title: {
      en: 'Jobs in Turku',
      fi: 'Työpaikat Turussa',
      sv: 'Jobb i Åbo'
    },
    description: {
      en: 'Explore job opportunities in Turku, Finland\'s oldest city and a major port. Find positions in maritime industries, healthcare, education, and emerging tech sectors in this historic coastal city.',
      fi: 'Tutki työmahdollisuuksia Turussa, Suomen vanhimmassa kaupungissa ja merkittävässä satamassa. Löydä työpaikkoja merenkulkualoilta, terveydenhuollosta, koulutuksesta ja nousevista teknologia-aloista tässä historiallisessa rannikkokaupungissa.',
      sv: 'Utforska jobbmöjligheter i Åbo, Finlands äldsta stad och en stor hamn. Hitta positioner inom maritima industrier, hälsovård, utbildning och framväxande tekniska sektorer i denna historiska kuststaden.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/oulu_finland',
      fi: '/fi/tyopaikat/alue/oulu_suomi',
      sv: '/sv/lediga-jobb/omrade/uleaborg_finland'
    },
    title: {
      en: 'Jobs in Oulu',
      fi: 'Työpaikat Oulussa',
      sv: 'Jobb i Uleåborg'
    },
    description: {
      en: 'Find jobs in Oulu, Northern Finland\'s tech capital with a strong focus on ICT, cleantech, and wellbeing technologies. Join a thriving ecosystem of innovation and research.',
      fi: 'Löydä työpaikkoja Oulusta, Pohjois-Suomen teknologiapääkaupungista, jossa keskitytään vahvasti ICT:hen, puhtaaseen teknologiaan ja hyvinvointiteknologioihin. Liity kukoistavaan innovaatio- ja tutkimusekosysteemiin.',
      sv: 'Hitta jobb i Uleåborg, norra Finlands teknikkapital med stark fokus på IKT, cleantech och välfärdsteknologier. Gå med i ett blomstrande ekosystem av innovation och forskning.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/jyvaskyla_finland',
      fi: '/fi/tyopaikat/alue/jyvaskyla_suomi',
      sv: '/sv/lediga-jobb/omrade/jyvaskyla_finland'
    },
    title: {
      en: 'Jobs in Jyväskylä',
      fi: 'Työpaikat Jyväskylässä',
      sv: 'Jobb i Jyväskylä'
    },
    description: {
      en: 'Discover opportunities in Jyväskylä, a vibrant university city in Central Finland. Known for education, research, and paper industry, find your next role in this dynamic lakeside city.',
      fi: 'Löydä mahdollisuuksia Jyväskylästä, elinvoimaisesta yliopistokaupungista Keski-Suomessa. Koulutuksestaan, tutkimuksestaan ja paperiteollisuudestaan tunnettu - löydä seuraava roolisi tästä dynaamisesta järven rannalla sijaitsevasta kaupungista.',
      sv: 'Upptäck möjligheter i Jyväskylä, en livlig universitetsstad i Mellersta Finland. Känd för utbildning, forskning och pappersindustri, hitta din nästa roll i denna dynamiska sjöstad.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/lahti_finland',
      fi: '/fi/tyopaikat/alue/lahti_suomi',
      sv: '/sv/lediga-jobb/omrade/lahtis_finland'
    },
    title: {
      en: 'Jobs in Lahti',
      fi: 'Työpaikat Lahdessa',
      sv: 'Jobb i Lahtis'
    },
    description: {
      en: 'Find career opportunities in Lahti, a growing city known for environmental innovation and circular economy. Explore positions in green technology, design, and sustainable business.',
      fi: 'Löydä uramahdollisuuksia Lahdesta, kasvavasta kaupungista, joka tunnetaan ympäristöinnovaatioistaan ja kiertotaloudesta. Tutustu työpaikkoihin vihreässä teknologiassa, muotoilussa ja kestävässä liiketoiminnassa.',
      sv: 'Hitta karriärmöjligheter i Lahtis, en växande stad känd för miljöinnovation och cirkulär ekonomi. Utforska positioner inom grön teknologi, design och hållbar affärsverksamhet.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/kuopio_finland',
      fi: '/fi/tyopaikat/alue/kuopio_suomi',
      sv: '/sv/lediga-jobb/omrade/kuopio_finland'
    },
    title: {
      en: 'Jobs in Kuopio',
      fi: 'Työpaikat Kuopiossa',
      sv: 'Jobb i Kuopio'
    },
    description: {
      en: 'Explore job openings in Kuopio, Eastern Finland\'s regional capital and a center for health technology and wellbeing. Find opportunities in healthcare, food industry, and technology sectors.',
      fi: 'Tutki työpaikkoja Kuopiosta, Itä-Suomen aluekeskuksesta ja terveysteknologian sekä hyvinvoinnin keskuksesta. Löydä mahdollisuuksia terveydenhuollosta, elintarviketeollisuudesta ja teknologia-aloilta.',
      sv: 'Utforska jobböppningar i Kuopio, östra Finlands regionala huvudstad och ett centrum för hälsoteknik och välbefinnande. Hitta möjligheter inom hälsovård, livsmedelsindustri och teknologisektorer.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/pori_finland',
      fi: '/fi/tyopaikat/alue/pori_suomi',
      sv: '/sv/lediga-jobb/omrade/bjorneborg_finland'
    },
    title: {
      en: 'Jobs in Pori',
      fi: 'Työpaikat Porissa',
      sv: 'Jobb i Björneborg'
    },
    description: {
      en: 'Discover career opportunities in Pori, a coastal city with strong industry and energy sectors. Find positions in energy, manufacturing, logistics, and cultural industries.',
      fi: 'Löydä uramahdollisuuksia Porista, rannikkokaupungista, jossa on vahva teollisuus- ja energiasektori. Löydä työpaikkoja energiasta, valmistuksesta, logistiikasta ja kulttuurialoilta.',
      sv: 'Upptäck karriärmöjligheter i Björneborg, en kuststad med starka industri- och energisektorer. Hitta positioner inom energi, tillverkning, logistik och kulturindustrier.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/kotka_finland',
      fi: '/fi/tyopaikat/alue/kotka_suomi',
      sv: '/sv/lediga-jobb/omrade/kotka_finland'
    },
    title: {
      en: 'Jobs in Kotka',
      fi: 'Työpaikat Kotkassa',
      sv: 'Jobb i Kotka'
    },
    description: {
      en: 'Discover career opportunities in Kotka, a major port city on the Gulf of Finland specializing in logistics, maritime industries, and international trade. With its strategic coastal location and strong industrial base, Kotka offers excellent opportunities in transportation and manufacturing sectors.',
      fi: 'Löydä uramahdollisuuksia Kotkasta, Suomenlahden tärkeästä satamakaupungista, joka erikoistuu logistiikkaan, meriteollisuuteen ja kansainväliseen kauppaan. Strategisella rannikkosijainnillaan ja vahvalla teollisuusperinteellä Kotka tarjoaa erinomaisia mahdollisuuksia liikenteen ja valmistavan teollisuuden aloilla.',
      sv: 'Upptäck karriärmöjligheter i Kotka, en viktig hamnstad vid Finska viken som specialiserar sig på logistik, sjöindustri och internationell handel. Med sin strategiska kustplats och starka industriella bas erbjuder Kotka utmärkta möjligheter inom transport och tillverkningssektorer.'
    },
    lastModified: '2025-01-27T00:00:00Z'
  },
  // ---------------------------------------------------------------------------
  // Second wave of Finnish city hubs (TEC-423 follow-up). These pages were already
  // built, live and `index,follow`, but were invisible to Google because nothing
  // outside this allowlist enters the sitemap.
  //
  // The `sv` slugs below are the ones the site actually serves and self-canonicalises
  // to today — verified against production. Porvoo/Rauma/Kokkola keep their *Finnish*
  // slug in `sv` because CITY_MAPPINGS in utils/location/generate-localized-location-url.ts
  // has no row for them, even though the pages render "Borgå"/"Raumo"/"Karleby" in the
  // H1. Do not "fix" these to the Swedish exonym here alone: getSRPHub() drives the
  // canonical, so a slug that disagrees with generateLocalizedLocationUrl() would point
  // the sitemap at a URL the rest of the site never links to.
  // ---------------------------------------------------------------------------
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/rovaniemi_finland',
      fi: '/fi/tyopaikat/alue/rovaniemi_suomi',
      sv: '/sv/lediga-jobb/omrade/rovaniemi_finland'
    },
    title: {
      en: 'Jobs in Rovaniemi',
      fi: 'Työpaikat Rovaniemellä',
      sv: 'Jobb i Rovaniemi'
    },
    description: {
      en: 'Explore job openings in Rovaniemi, capital of Finnish Lapland. Find roles in tourism, healthcare, education and Arctic research in Europe\'s northernmost regional centre.',
      fi: 'Tutki avoimia työpaikkoja Rovaniemellä, Suomen Lapin pääkaupungissa. Löydä tehtäviä matkailusta, terveydenhuollosta, koulutuksesta ja arktisesta tutkimuksesta.',
      sv: 'Utforska lediga tjänster i Rovaniemi, huvudstaden i Finska Lappland. Hitta roller inom turism, vård, utbildning och arktisk forskning.'
    },
    lastModified: '2026-07-30T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/vaasa_finland',
      fi: '/fi/tyopaikat/alue/vaasa_suomi',
      sv: '/sv/lediga-jobb/omrade/vasa_finland'
    },
    title: {
      en: 'Jobs in Vaasa',
      fi: 'Työpaikat Vaasassa',
      sv: 'Jobb i Vasa'
    },
    description: {
      en: 'Discover career opportunities in Vaasa, the bilingual heart of the Nordic energy technology cluster. Find roles in energy, engineering, manufacturing and international trade.',
      fi: 'Löydä uramahdollisuuksia Vaasasta, kaksikielisestä rannikkokaupungista ja Pohjoismaiden energiateknologian keskuksesta. Tehtäviä energia-alalta, tekniikasta ja teollisuudesta.',
      sv: 'Upptäck karriärmöjligheter i Vasa, det tvåspråkiga navet för nordisk energiteknik. Hitta roller inom energi, teknik, tillverkning och internationell handel.'
    },
    lastModified: '2026-07-30T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/kouvola_finland',
      fi: '/fi/tyopaikat/alue/kouvola_suomi',
      sv: '/sv/lediga-jobb/omrade/kouvola_finland'
    },
    title: {
      en: 'Jobs in Kouvola',
      fi: 'Työpaikat Kouvolassa',
      sv: 'Jobb i Kouvola'
    },
    description: {
      en: 'Find jobs in Kouvola, a key transport and logistics hub in southeastern Finland. Explore openings in logistics, rail freight, manufacturing and public services.',
      fi: 'Löydä työpaikkoja Kouvolasta, Kaakkois-Suomen tärkeästä kuljetus- ja logistiikkakeskuksesta. Tutustu tehtäviin logistiikassa, teollisuudessa ja julkisilla aloilla.',
      sv: 'Hitta jobb i Kouvola, ett viktigt transport- och logistiknav i sydöstra Finland. Utforska tjänster inom logistik, tillverkning och offentlig sektor.'
    },
    lastModified: '2026-07-30T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/seinajoki_finland',
      fi: '/fi/tyopaikat/alue/seinajoki_suomi',
      sv: '/sv/lediga-jobb/omrade/seinajoki_finland'
    },
    title: {
      en: 'Jobs in Seinäjoki',
      fi: 'Työpaikat Seinäjoella',
      sv: 'Jobb i Seinäjoki'
    },
    description: {
      en: 'Explore job opportunities in Seinäjoki, the regional centre of South Ostrobothnia. Find roles in food processing, agriculture, manufacturing and growing technology sectors.',
      fi: 'Tutki työmahdollisuuksia Seinäjoella, Etelä-Pohjanmaan maakuntakeskuksessa. Löydä tehtäviä elintarviketeollisuudesta, maataloudesta, valmistuksesta ja teknologia-aloilta.',
      sv: 'Utforska jobbmöjligheter i Seinäjoki, regioncentrum i Södra Österbotten. Hitta roller inom livsmedelsförädling, jordbruk, tillverkning och växande tekniksektorer.'
    },
    lastModified: '2026-07-30T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/hameenlinna_finland',
      fi: '/fi/tyopaikat/alue/hameenlinna_suomi',
      sv: '/sv/lediga-jobb/omrade/tavastehus_finland'
    },
    title: {
      en: 'Jobs in Hämeenlinna',
      fi: 'Työpaikat Hämeenlinnassa',
      sv: 'Jobb i Tavastehus'
    },
    description: {
      en: 'Discover open positions in Hämeenlinna, a historic city and industrial centre between Helsinki and Tampere. Find roles in manufacturing, healthcare, logistics and public services.',
      fi: 'Löydä avoimia työpaikkoja Hämeenlinnasta, historiallisesta kaupungista ja teollisuuskeskuksesta Helsingin ja Tampereen välillä. Tehtäviä teollisuudesta, terveydenhuollosta ja logistiikasta.',
      sv: 'Upptäck lediga tjänster i Tavastehus, en historisk stad och ett industricentrum mellan Helsingfors och Tammerfors. Roller inom tillverkning, vård och logistik.'
    },
    lastModified: '2026-07-30T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/porvoo_finland',
      fi: '/fi/tyopaikat/alue/porvoo_suomi',
      sv: '/sv/lediga-jobb/omrade/porvoo_finland'
    },
    title: {
      en: 'Jobs in Porvoo',
      fi: 'Työpaikat Porvoossa',
      sv: 'Jobb i Borgå'
    },
    description: {
      en: 'Find career opportunities in Porvoo, a historic coastal town near Helsinki with a major refining and energy sector. Explore roles in energy, industry, tourism and services.',
      fi: 'Löydä uramahdollisuuksia Porvoosta, historiallisesta rannikkokaupungista lähellä Helsinkiä. Tutustu tehtäviin energia-alalla, teollisuudessa, matkailussa ja palveluissa.',
      sv: 'Hitta karriärmöjligheter i Borgå, en historisk kuststad nära Helsingfors med stark raffinaderi- och energisektor. Roller inom energi, industri, turism och service.'
    },
    lastModified: '2026-07-30T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/mikkeli_finland',
      fi: '/fi/tyopaikat/alue/mikkeli_suomi',
      sv: '/sv/lediga-jobb/omrade/st_michel_finland'
    },
    title: {
      en: 'Jobs in Mikkeli',
      fi: 'Työpaikat Mikkelissä',
      sv: 'Jobb i S:t Michel'
    },
    description: {
      en: 'Explore job openings in Mikkeli, a lakeside regional capital in Southern Savonia. Find roles in healthcare, education, defence technology and public administration.',
      fi: 'Tutki avoimia työpaikkoja Mikkelissä, Etelä-Savon järvenrantakaupungissa. Löydä tehtäviä terveydenhuollosta, koulutuksesta, puolustusteknologiasta ja hallinnosta.',
      sv: 'Utforska lediga tjänster i S:t Michel, en regionhuvudstad vid sjön i Södra Savolax. Hitta roller inom vård, utbildning, försvarsteknik och förvaltning.'
    },
    lastModified: '2026-07-30T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/lappeenranta_finland',
      fi: '/fi/tyopaikat/alue/lappeenranta_suomi',
      sv: '/sv/lediga-jobb/omrade/villmanstrand_finland'
    },
    title: {
      en: 'Jobs in Lappeenranta',
      fi: 'Työpaikat Lappeenrannassa',
      sv: 'Jobb i Villmanstrand'
    },
    description: {
      en: 'Discover jobs in Lappeenranta, a lakeside university city known for clean technology research. Find roles in engineering, cleantech, forestry and higher education.',
      fi: 'Löydä työpaikkoja Lappeenrannasta, järvenrantayliopistokaupungista, joka tunnetaan puhtaan teknologian tutkimuksesta. Tehtäviä tekniikasta, cleantechistä ja metsäalalta.',
      sv: 'Upptäck jobb i Villmanstrand, en universitetsstad vid Saimen känd för forskning inom ren teknik. Roller inom teknik, cleantech, skogsbruk och utbildning.'
    },
    lastModified: '2026-07-30T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/rauma_finland',
      fi: '/fi/tyopaikat/alue/rauma_suomi',
      sv: '/sv/lediga-jobb/omrade/rauma_finland'
    },
    title: {
      en: 'Jobs in Rauma',
      fi: 'Työpaikat Raumalla',
      sv: 'Jobb i Raumo'
    },
    description: {
      en: 'Find jobs in Rauma, a UNESCO World Heritage port city with deep maritime roots. Explore openings in marine industries, manufacturing, logistics and shipbuilding.',
      fi: 'Löydä työpaikkoja Raumalta, UNESCOn maailmanperintökohteena tunnetusta satamakaupungista. Tutustu tehtäviin meriteollisuudessa, valmistuksessa ja logistiikassa.',
      sv: 'Hitta jobb i Raumo, en hamnstad på UNESCOs världsarvslista med djupa sjöfartsrötter. Tjänster inom marinindustri, tillverkning och logistik.'
    },
    lastModified: '2026-07-30T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/kokkola_finland',
      fi: '/fi/tyopaikat/alue/kokkola_suomi',
      sv: '/sv/lediga-jobb/omrade/kokkola_finland'
    },
    title: {
      en: 'Jobs in Kokkola',
      fi: 'Työpaikat Kokkolassa',
      sv: 'Jobb i Karleby'
    },
    description: {
      en: 'Explore career opportunities in Kokkola, a bilingual coastal city built around chemical industry and clean energy. Find roles in chemicals, industry, logistics and cleantech.',
      fi: 'Tutki uramahdollisuuksia Kokkolasta, kaksikielisestä rannikkokaupungista, jonka ytimessä ovat kemianteollisuus ja puhdas energia. Tehtäviä teollisuudesta ja logistiikasta.',
      sv: 'Utforska karriärmöjligheter i Karleby, en tvåspråkig kuststad byggd kring kemisk industri och ren energi. Roller inom kemi, industri, logistik och cleantech.'
    },
    lastModified: '2026-07-30T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/kemi_finland',
      fi: '/fi/tyopaikat/alue/kemi_suomi',
      sv: '/sv/lediga-jobb/omrade/kemi_finland'
    },
    title: {
      en: 'Jobs in Kemi',
      fi: 'Työpaikat Kemissä',
      sv: 'Jobb i Kemi'
    },
    description: {
      en: 'Discover open positions in Kemi, a northern port city driven by the forest industry. Find roles in pulp and paper, manufacturing, logistics and port operations.',
      fi: 'Löydä avoimia työpaikkoja Kemistä, pohjoisesta satamakaupungista, jota metsäteollisuus vauhdittaa. Tehtäviä sellu- ja paperiteollisuudessa, valmistuksessa ja logistiikassa.',
      sv: 'Upptäck lediga tjänster i Kemi, en nordlig hamnstad driven av skogsindustrin. Roller inom massa och papper, tillverkning, logistik och hamnverksamhet.'
    },
    lastModified: '2026-07-30T00:00:00Z'
  },
  // ---------------------------------------------------------------------------
  // Third wave (Aug 2026 search report follow-up). Salo (74 live results on the
  // exact `location:="Salo, Suomi"` facet, 2.4K searches) was missing from every
  // location config. Joensuu stays held back with Hanko (62 listings, 400
  // searches): the raw-listing counts that justified it (31 → 69) count jobs the
  // SRP's exact location-facet filter never matches (bare "Joensuu", multi-city
  // strings, description-only mentions) — the live hub renders only ~11 results
  // (fi 11 / en 12 / sv 9), too thin to index against a ~14.6K/mo head term.
  // Re-add only once the exact-facet count clears the second-wave bar.
  // Salo has no distinct Swedish exonym, so the sv slug reuses the
  // Finnish/English name — consistent with what production self-canonicalises to.
  // ---------------------------------------------------------------------------
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/location/salo_finland',
      fi: '/fi/tyopaikat/alue/salo_suomi',
      sv: '/sv/lediga-jobb/omrade/salo_finland'
    },
    title: {
      en: 'Jobs in Salo',
      fi: 'Työpaikat Salossa',
      sv: 'Jobb i Salo'
    },
    description: {
      en: 'Find jobs in Salo, a Southwest Finland technology town reinventing itself around IoT, batteries and smart manufacturing. Explore roles in technology, industry and services.',
      fi: 'Löydä työpaikkoja Salosta, Varsinais-Suomen teknologiakaupungista, joka uudistuu IoT:n, akkuteollisuuden ja älykkään valmistuksen ympärillä. Tehtäviä teknologiasta, teollisuudesta ja palveluista.',
      sv: 'Hitta jobb i Salo, en teknikstad i Egentliga Finland som förnyar sig kring IoT, batterier och smart tillverkning. Roller inom teknik, industri och service.'
    },
    lastModified: '2026-08-06T00:00:00Z'
  }
];

/**
 * Role (normalisedTitle facet) hubs, generated from ROLE_HUB_CONFIG, the single source of truth
 * for slug/displayName, the same way categoryHubs below is generated from CATEGORY_HUB_CONFIG.
 * The previous hand-duplicated two-entry list is what let role hubs drift. The two original
 * roles keep their hand-written titles/descriptions via utils/seo/url-static-seo-meta.ts, which
 * overrides whatever is generated here.
 */
export const roleHubs: SRPHub[] = ROLE_HUB_CONFIG.map((role) => ({
  type: 'role' as const,
  localeUrls: {
    en: buildRoleHubPath(role.slug, 'en'),
    fi: buildRoleHubPath(role.slug, 'fi'),
    sv: buildRoleHubPath(role.slug, 'sv'),
  },
  title: {
    en: `${role.displayNames.en} Jobs in Finland`,
    fi: `${getFinnishRoleGenitive(role.displayNames.fi)} työpaikat`,
    sv: `Lediga jobb: ${role.displayNames.sv}`,
  },
  description: {
    en: `Find ${role.displayNames.en.toLowerCase()} jobs across Finland from company career pages and Työmarkkinatori in one search, updated daily.`,
    fi: `Löydä avoimet ${getFinnishRoleGenitive(role.displayNames.fi).toLowerCase()} työpaikat koko Suomesta yritysten urasivuilta ja Työmarkkinatorilta yhdellä haulla. Päivitetty päivittäin.`,
    sv: `Hitta lediga jobb som ${role.displayNames.sv.toLowerCase()} i hela Finland från företags karriärsidor och Työmarkkinatori i en sökning. Uppdateras dagligen.`,
  },
  lastModified: '2026-09-04T00:00:00Z',
}));

/**
 * Remote work hubs
 */
export const remoteHubs: SRPHub[] = [
  {
    // Work-model hub (remoteness facet), not a pseudo-location: see utils/work-model/work-model-hub.ts.
    type: 'location',
    localeUrls: {
      en: '/en/jobs/remote',
      fi: '/fi/tyopaikat/etatyo',
      sv: '/sv/lediga-jobb/distans'
    },
    title: {
      en: 'Remote Jobs in Finland',
      fi: 'Etätyöpaikat Suomessa',
      sv: 'Distansjobb i Finland'
    },
    description: {
      en: 'Every fully remote job open to people in Finland, straight from employers and Työmarkkinatori.',
      fi: 'Kaikki kokonaan etänä tehtävät työpaikat Suomessa suoraan työnantajilta ja Työmarkkinatorilta.',
      sv: 'Alla helt distansbaserade jobb i Finland, direkt från arbetsgivare och Työmarkkinatori.'
    },
    lastModified: '2026-09-04T00:00:00Z'
  },
  {
    type: 'location',
    localeUrls: {
      en: '/en/jobs/hybrid',
      fi: '/fi/tyopaikat/hybridityo',
      sv: '/sv/lediga-jobb/hybridjobb'
    },
    title: {
      en: 'Hybrid Jobs in Finland',
      fi: 'Hybridityöpaikat Suomessa',
      sv: 'Hybridjobb i Finland'
    },
    description: {
      en: 'Hybrid jobs in Finland: part office, part remote, straight from employers and Työmarkkinatori.',
      fi: 'Hybridityöpaikat Suomessa: osin toimistolla, osin etänä, suoraan työnantajilta ja Työmarkkinatorilta.',
      sv: 'Hybridjobb i Finland: delvis på kontoret, delvis på distans, direkt från arbetsgivare och Työmarkkinatori.'
    },
    lastModified: '2026-09-04T00:00:00Z'
  }
];

/**
 * Category ("field" facet) hubs — generated from CATEGORY_HUB_CONFIG (the single source of
 * truth for slug/displayName), NOT hand-duplicated per entry like `roleHubs` above. That
 * duplication is exactly what let `roleHubs` drift out of sync and go orphaned (its `/jobs/role/…`
 * URLs never matched a real route). Phase 1 ships the first 10 categories (by job volume,
 * matching CATEGORY_HUB_CONFIG's order) to unblock TEC-423's GSC submission; the remaining
 * categories are added in phase 2.
 */
const CATEGORY_HUBS_PRIORITY_COUNT = 10;

/**
 * Categories whose inventory is exactly served by an allowlisted role hub.
 * `healthcare-assistant` shares identical en/sv fieldValues with
 * `/jobs/role/lahihoitaja` (fi differs only by absorbing Hoiva-Avustaja), so
 * indexing both would ship two self-canonical pages with the same jobs
 * competing for the same queries. The role hub is the search-demand target
 * (lähihoitaja työpaikat, 4.1K/mo) and wins; the category page stays live but
 * noindexed. (`nurse` vs role/sairaanhoitaja needs no entry here — that
 * category is already outside the priority-10 slice.)
 */
const CATEGORY_HUBS_SUPERSEDED_BY_ROLE_HUBS = new Set(['healthcare-assistant']);

export const categoryHubs: SRPHub[] = CATEGORY_HUB_CONFIG.slice(0, CATEGORY_HUBS_PRIORITY_COUNT)
  .filter((category) => !CATEGORY_HUBS_SUPERSEDED_BY_ROLE_HUBS.has(category.slug))
  .map((category) => ({
  type: 'category' as const,
  localeUrls: {
    en: `/en/jobs/category/${category.slug}`,
    fi: `/fi/tyopaikat/kategoria/${category.slug}`,
    sv: `/sv/lediga-jobb/kategori/${category.slug}`,
  },
  title: {
    en: `${category.displayNames.en} Jobs in Finland`,
    fi: `${category.displayNames.fi} | työpaikat`,
    sv: `${category.displayNames.sv} | jobb i Finland`,
  },
  description: {
    en: `Find ${category.displayNames.en} jobs in Finland. Browse open positions updated daily on JobCrawls.`,
    fi: `Löydä ${category.displayNames.fi.toLowerCase()}-alan työpaikkoja Suomessa. Selaa avoimia työpaikkoja, jotka päivittyvät päivittäin JobCrawlsissa.`,
    sv: `Hitta ${category.displayNames.sv}-jobb i Finland. Bläddra bland lediga tjänster som uppdateras dagligen på JobCrawls.`,
  },
  lastModified: '2026-07-21T00:00:00Z',
}));

/**
 * Freelance (jobType facet) hub — a single fixed value, unlike category/role which are
 * generated from a curated slug config. See utils/freelance/ for the stats/copy generators.
 */
export const freelanceHubs: SRPHub[] = [
  {
    type: 'freelance',
    localeUrls: {
      en: '/en/jobs/freelance',
      fi: '/fi/tyopaikat/keikkatyo',
      sv: '/sv/lediga-jobb/frilans',
    },
    title: {
      en: 'Freelance Jobs in Finland',
      fi: 'Keikkatyöpaikat Suomessa',
      sv: 'Frilansjobb i Finland',
    },
    description: {
      en: 'Find freelance jobs in Finland. Browse open freelance positions updated daily on JobCrawls.',
      fi: 'Löydä keikkatyöpaikkoja Suomessa. Selaa avoimia keikkatyöpaikkoja, jotka päivittyvät päivittäin JobCrawlsissa.',
      sv: 'Hitta frilansjobb i Finland. Bläddra bland lediga frilansuppdrag som uppdateras dagligen på JobCrawls.',
    },
    lastModified: '2026-08-28T00:00:00Z',
  },
];

/**
 * Complete allowlist of indexable SRPs
 */
export const SRP_ALLOWLIST: SRPHub[] = [
  ...cityHubs,
  ...roleHubs,
  ...remoteHubs,
  ...categoryHubs,
  ...freelanceHubs,
];

function normalizeAllowlistPath(path: string): string {
  const trimmed = path === '/' ? '/' : path.replace(/\/$/, '');
  return encodeSrpPathForUrl(trimmed);
}

// Case-insensitive: lowercase is canonical, but inbound requests may arrive in any casing
// (before a redirect normalizes them) and must still resolve to the right allowlisted hub.
function hubPathsMatch(path: string, hubPath: string): boolean {
  return normalizeAllowlistPath(path).toLowerCase() === normalizeAllowlistPath(hubPath).toLowerCase();
}

/**
 * Check if a given path is in the allowlist
 */
export function isAllowlistedSRP(path: string): boolean {
  const normalizedPath = path === '/' ? '/' : path.replace(/\/$/, '');

  return SRP_ALLOWLIST.some((hub) =>
    hubPathsMatch(normalizedPath, hub.localeUrls.en) ||
    hubPathsMatch(normalizedPath, hub.localeUrls.fi) ||
    hubPathsMatch(normalizedPath, hub.localeUrls.sv),
  );
}

/**
 * Get SRP hub data for a given path
 */
export function getSRPHub(path: string): SRPHub | null {
  const normalizedPath = path === '/' ? '/' : path.replace(/\/$/, '');

  return SRP_ALLOWLIST.find((hub) =>
    hubPathsMatch(normalizedPath, hub.localeUrls.en) ||
    hubPathsMatch(normalizedPath, hub.localeUrls.fi) ||
    hubPathsMatch(normalizedPath, hub.localeUrls.sv),
  ) || null;
}

/**
 * Get hreflang cluster for a given SRP
 */
export function getHreflangCluster(path: string, baseUrl: string): Array<{ href: string; hrefLang: string }> | null {
  const hub = getSRPHub(path);
  if (!hub) return null;
  
  return [
    { href: `${baseUrl}${encodeSrpPathForUrl(hub.localeUrls.en)}`, hrefLang: 'en' },
    { href: `${baseUrl}${encodeSrpPathForUrl(hub.localeUrls.fi)}`, hrefLang: 'fi' },
    { href: `${baseUrl}${encodeSrpPathForUrl(hub.localeUrls.sv)}`, hrefLang: 'sv' },
    { href: `${baseUrl}${encodeSrpPathForUrl(hub.localeUrls.en)}`, hrefLang: 'x-default' },
  ];
}

